import { FC, useState } from "react";
import { MdLock } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSetIncidentDispositionMutation } from "../../../api/network/incidentApiServices";
import { AdminFormFieldDropdown } from "../../../components/admin/formFields";
import FTModal from "../../../components/common/FTModal";
import svgIcons from "../../../assets/svg/svgIcons";
import { useAccountTimezone } from "../../../hooks/timezone";
import { formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import {
  INCIDENT_DISPOSITIONS,
  INCIDENT_DISPOSITIONS_REQUIRING_COMMENT,
  IncidentDetailResponse,
  IncidentDispositionResponse,
  TIncidentDisposition,
} from "./type";

type IncidentDetailDispositionBarProps = {
  incident: IncidentDetailResponse;
  onDispositionChanged: (updated: IncidentDispositionResponse) => void;
};

const FALLBACK_DISPOSITION = { label: "Unknown", color: "#6B7280" };

const IncidentDetailDispositionBar: FC<IncidentDetailDispositionBarProps> = ({
  incident,
  onDispositionChanged,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.disposition_bar",
  });
  const { tzForDateFns } = useAccountTimezone();

  const [showModal, setShowModal] = useState(false);
  const [pendingDisposition, setPendingDisposition] = useState<TIncidentDisposition | null>(null);
  const [comment, setComment] = useState("");

  const [setDisposition, { isLoading: isSaving }] = useSetIncidentDispositionMutation();

  const currentDisposition = incident.disposition;
  const requiresComment =
    !!pendingDisposition && INCIDENT_DISPOSITIONS_REQUIRING_COMMENT.includes(pendingDisposition);
  // once an incident is Confirmed the disposition is final - no further changes allowed
  const isLocked = currentDisposition === "confirmed";

  const openModal = () => {
    if (isLocked) return;
    setPendingDisposition(null);
    setComment("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setPendingDisposition(null);
    setComment("");
  };

  const canSave =
    !!pendingDisposition &&
    (!requiresComment || comment.trim().length > 0) &&
    !isSaving;

  const handleSave = () => {
    if (!pendingDisposition || !canSave) return;
    setDisposition({
      recordid: incident.recordid,
      disposition: pendingDisposition,
      comment: comment.trim(),
    })
      .unwrap()
      .then((response) => {
        onDispositionChanged(response);
        toast.success(t("save_success"));
        closeModal();
      })
      .catch(() => {
        toast.error(t("save_error"));
      });
  };

  const otherDispositionOptions = (Object.keys(INCIDENT_DISPOSITIONS) as TIncidentDisposition[])
    .filter((value) => value !== currentDisposition)
    .map((value) => ({ value, label: INCIDENT_DISPOSITIONS[value].label }));

  const currentDispositionMeta = INCIDENT_DISPOSITIONS[currentDisposition] ?? FALLBACK_DISPOSITION;
  const currentColor = currentDispositionMeta.color;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openModal}
        disabled={isLocked}
        title={isLocked ? t("locked_tooltip") : undefined}
        className="h-9 px-4 whitespace-nowrap rounded-md border text-[13px] font-display font-semibold transition enabled:hover:brightness-95 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        style={{
          backgroundColor: `${currentColor}14`,
          borderColor: `${currentColor}66`,
          color: currentColor,
        }}
      >
        {t("current_status")} {currentDispositionMeta.label}
        {isLocked && <MdLock className="size-3.5 shrink-0" />}
      </button>

      {!!incident.disposition_set_by && !!incident.disposition_set_at && (
        <div className="mt-1 text-right">
          <span className="block text-[11px] text-heading-gray666">
            {t("reviewed_by", {
              name: incident.disposition_set_by,
              date: formattedDateTimeBasedOnTimezone(incident.disposition_set_at, tzForDateFns),
            })}
          </span>
          {!!incident.disposition_comment && (
            <span className="block text-[11px] text-heading-gray666 italic ml-auto max-w-[260px]">
              "{incident.disposition_comment}"
            </span>
          )}
        </div>
      )}

      <FTModal
        visible={showModal}
        onHide={closeModal}
        position="center"
        containerStyle="w-[400px] max-w-[calc(100vw-2rem)]"
        CustomHeadingWrapper={() => (
          <div className="pr-6">
            <div className="text-lg font-bold text-heading-black">{t("popup_heading")}</div>
            <p className="text-[13px] leading-5 text-heading-gray666 mt-1.5">{t("popup_subheading")}</p>
          </div>
        )}
        heading={t("popup_heading")}
      >
        <div className="flex flex-col gap-5 w-full pt-1">
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-2 shrink-0">
              <span className="text-sm font-semibold text-heading-black">{t("current_status_label")}</span>
              <span
                className="inline-flex w-fit items-center px-3 py-1.5 rounded-md border text-[13px] font-medium"
                style={{
                  backgroundColor: `${currentColor}14`,
                  borderColor: `${currentColor}66`,
                  color: currentColor,
                }}
              >
                {currentDispositionMeta.label}
              </span>
            </div>
            <img src={svgIcons.RightArrowIcon} alt="" className="size-4 mt-9 shrink-0 opacity-70" />
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <span className="text-sm font-semibold text-heading-black">{t("new_disposition_label")}</span>
              <AdminFormFieldDropdown
                label={false}
                id="new-disposition"
                name="new-disposition"
                placeholder={t("select_placeholder")}
                value={pendingDisposition ?? undefined}
                options={otherDispositionOptions}
                onChange={(option) => {
                  const value = (option?.value as TIncidentDisposition) ?? null;
                  setPendingDisposition(value);
                  setComment("");
                }}
              />
            </div>
          </div>

          {!!incident.disposition_comment && (
            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-md p-3">
              <span className="block text-[11px] font-semibold text-heading-gray666 uppercase tracking-wide mb-1">
                {t("previous_note_label")}
              </span>
              <p className="text-[13px] leading-5 text-heading-black whitespace-pre-wrap">
                {incident.disposition_comment}
              </p>
            </div>
          )}

          <div className="w-full">
            <label htmlFor="disposition-comment" className="block text-sm font-semibold text-heading-black">
              {requiresComment ? `${t("comment_label")} *` : t("comment_label")}
            </label>
            <textarea
              id="disposition-comment"
              name="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("comment_placeholder")}
              className="mt-1 w-full h-24 rounded-md border border-[#E5E7EB] bg-[#F8F9FA] p-3 text-sm resize-none focus:outline-none focus:border-accent-blue-dark"
            />
            {requiresComment && (
              <span className="block text-[10px] text-gray-400 mt-1">{t("comment_required_hint")}</span>
            )}
          </div>

          <div className="flex flex-col gap-3 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="w-full h-11 rounded-lg bg-accent-blue-pale text-accent-blue-dark text-sm font-semibold transition enabled:hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t("saving") : t("save")}
            </button>
            <button
              type="button"
              onClick={closeModal}
              className="text-field-error-dark text-[13px] font-semibold text-center enabled:hover:underline"
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      </FTModal>
    </div>
  );
};

export default IncidentDetailDispositionBar;
